import { CommonModule } from '@angular/common';
import {
  Component,
  signal,
  inject,
  DestroyRef,
  ElementRef,
  computed,
} from '@angular/core';
import { ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { fromEvent, interval } from 'rxjs';
import { filter, startWith } from 'rxjs/operators';

import { VOneIconComponent } from '@shared-components';
import { IconType } from '@shared-enums';
import { CompanyOption } from '@shared-layouts';
import { AlertService } from '@shared-services';

import { IStorageCompany } from 'src/app/@interfaces/company.interface';
import { IUserSeller } from 'src/app/@interfaces/userSeller.interface';
import { BillyService } from 'src/app/@pages/billyService/billy.service';
import { UsersService } from 'src/app/@pages/users/users.service';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import { AppState } from 'src/app/state/app.state';
import { selectCompaniesList } from 'src/app/state/selectors/user-companies.selectors';

@Component({
  selector: 'app-company-selector',
  imports: [VOneIconComponent, CommonModule, TranslateModule],
  templateUrl: './company-selector.component.html',
  styleUrl: './company-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanySelectorComponent {
  private readonly cookieAuthService = inject(CookieAuthService);
  private readonly userService = inject(UsersService);
  private readonly billyService = inject(BillyService);
  private readonly alertService = inject(AlertService);
  private readonly store = inject(Store<AppState>);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef);

  private readonly companiesFromStore = toSignal(
    this.store.select(selectCompaniesList),
    { initialValue: [] },
  );

  companies = signal<CompanyOption[]>([]);
  selectedCompany = signal<CompanyOption | null>(null);
  isDropdownOpen = signal<boolean>(false);

  readonly shouldShowModal = computed(() => {
    return this.selectedCompany() === null && this.companies().length > 0;
  });

  readonly ICON_TYPE = IconType;

  constructor() {
    this.setupClickOutsideHandler();
    this.setupCompaniesListener();
    this.setupCompanyWatcher();
  }

  private setupClickOutsideHandler(): void {
    fromEvent<MouseEvent>(document, 'click')
      .pipe(
        filter(() => this.isDropdownOpen()),
        filter(() => !this.shouldShowModal()),
        filter(
          (event) =>
            !this.elementRef.nativeElement.contains(event.target as Node),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.isDropdownOpen.set(false);
      });
  }

  private setupCompanyWatcher(): void {
    interval(500)
      .pipe(startWith(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.checkAndUpdateSelectedCompany();
      });
  }

  private checkAndUpdateSelectedCompany(): void {
    // Don't update if there's no valid token
    const token = this.cookieAuthService.getToken;
    if (!token) {
      this.selectedCompany.set(null);
      return;
    }

    const currentCompany = this.cookieAuthService.getCompanyObject;
    const currentSelectedId = this.selectedCompany()?.id;

    if (currentCompany) {
      const newSelectedId = currentCompany.Id_company?.toString();

      if (newSelectedId !== currentSelectedId && this.companies().length > 0) {
        this.setSelectedCompany();
      }
    } else {
      // If there's no company object but we have companies, clear selection
      if (this.companies().length > 0 && this.selectedCompany() !== null) {
        this.selectedCompany.set(null);
      }
    }
  }

  onContainerKeydown(event: KeyboardEvent): void {
    if (this.shouldShowModal()) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
      }
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleDropdown();
    } else if (event.key === 'Escape') {
      this.isDropdownOpen.set(false);
    }
  }

  private setupCompaniesListener(): void {
    this.store
      .select(selectCompaniesList)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((storageCompanies) => {
        const companyOptions: CompanyOption[] = storageCompanies.map(
          (company) => ({
            id: company.Id_company!.toString(),
            name: company.name,
            logo: this.getCompanyLogo(company.Id_company!.toString()),
            isSelected: false,
          }),
        );

        this.companies.set(companyOptions);

        // Only try to set selected company if we have a valid token
        // This prevents loading stale data after logout
        const token = this.cookieAuthService.getToken;
        if (token) {
          this.setSelectedCompany();
        } else {
          // Ensure no company is selected if there's no token
          this.selectedCompany.set(null);
        }
      });
  }

  //todo, make this dynamic
  private getCompanyLogo(companyId: string): string {
    const logoMap: Record<string, string> = {
      '1': 'assets/img/v1rotateBlack.png', // V1-rotate-LLC
      '2': 'assets/img/Ibexports.png', // Ibexports
      '4': 'assets/img/Jens.png', // Jens Automotive Products
      '5': 'assets/icons/letrasVersa.png', // Versa Cars
      '6': 'assets/img/Parabricentro.png', // PARABRICENTRO DEL CARIBE S.A.S.
      '7': 'assets/img/v1rotateBlack.png', // V-One Colombia
      '8': 'assets/uplift/img/brand/v-one-insight-isotype.svg', // Comercializadora De Vidrios
      '9': 'assets/uplift/img/brand/v-one-insight-isotype.svg', // Tecnollantas Bogota
      '10': 'assets/uplift/img/companies/be-one-horizontal.svg', // V1-Insight Colombia
      '11': 'assets/uplift/img/brand/v-one-insight-isotype.svg', // EG Suministros Industriales
      '12': 'assets/uplift/img/brand/v-one-insight-isotype.svg', // Management Hospitality LLC
    };

    return (
      logoMap[companyId] || 'assets/uplift/img/brand/v-one-insight-isotype.svg'
    );
  }

  private setSelectedCompany(): void {
    // Don't set selected company if there's no valid token
    const token = this.cookieAuthService.getToken;
    if (!token) {
      this.selectedCompany.set(null);
      this.companies.update((companies) =>
        companies.map((c) => ({
          ...c,
          isSelected: false,
        })),
      );
      return;
    }

    const currentCompany = this.cookieAuthService.getCompanyObject;

    if (currentCompany && this.companies().length > 0) {
      const selectedId = currentCompany.Id_company?.toString();

      this.companies.update((companies) =>
        companies.map((c) => ({
          ...c,
          isSelected: c.id === selectedId,
        })),
      );

      const selected = this.companies().find((c) => c.id === selectedId);
      if (selected) {
        this.selectedCompany.set(selected);
      }
    } else if (!currentCompany) {
      this.selectedCompany.set(null);
      this.companies.update((companies) =>
        companies.map((c) => ({
          ...c,
          isSelected: false,
        })),
      );
    }
  }

  toggleDropdown(): void {
    if (this.shouldShowModal()) {
      return;
    }

    this.isDropdownOpen.update((state) => !state);
  }

  selectCompany(company: CompanyOption): void {
    this.isDropdownOpen.set(false);

    if (this.selectedCompany()?.id === company.id) {
      return;
    }

    this.onCompanyChange(company);
  }

  private onCompanyChange(company: CompanyOption): void {
    const selectedStorageCompany = this.companiesFromStore().find(
      (c) => c.Id_company?.toString() === company.id,
    );

    if (selectedStorageCompany) {
      this.performCompanySelection(selectedStorageCompany);
    }
  }

  private async performCompanySelection(
    company: IStorageCompany,
  ): Promise<void> {
    try {
      const userId = this.cookieAuthService.getUserId!;
      const userSellerId = await this.getUserSeller(
        parseInt(userId.toString()),
        company.Id_company!.toString(),
      );

      this.cookieAuthService.setLocalStorageCompany(company, userSellerId);
      this.cookieAuthService.setUserSellerId();
      this.cookieAuthService.setCompanyObject();
      this.billyService.assignToken();

      const initialRoute = await this.userService.getInitialRouteByRole();
      const currentUrl = this.router.url;
      if (currentUrl.includes(initialRoute)) {
        await this.router.navigate(['/selectDB']).then(() => {
          this.router.navigate([initialRoute]);
        });
      } else {
        await this.router.navigate([initialRoute]);
      }
    } catch (error) {
      console.error('Error changing company:', error);
      this.setSelectedCompany();
      this.alertService.open({
        title: 'Error',
        message: 'Failed to change company. Please try again.',
        type: 'error',
        acceptText: 'OK',
        showIcon: true,
      });
    }
  }

  private async getUserSeller(
    idUser: number,
    company: string,
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.userService
        .getSellerByUser(idUser, company)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (result: IUserSeller[]) => {
            if (result[0]) {
              resolve(result[0].id_userSeller.toString());
            } else {
              resolve('');
            }
          },
          error: (error) => {
            reject(error);
          },
        });
    });
  }

  capitalizeNameCompany(company?: string): string {
    if (!company) return '';

    return company
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
